import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import Tilt from "react-parallax-tilt";

import Particle from "../Particle";
import Testimonials from "./Testimonials";
import reviews from "./data";
import useInViewOnce from "../../hooks/useInViewOnce";

import techpalPortrait from "../../Assets/syep14.png";

import syepMain from "../../Assets/syepMain.png";
import cf_1 from "../../Assets/syep_2025_1.jpg";
import syep2 from "../../Assets/syep_2025_2.jpg";
import techpal1 from "../../Assets/techPal1.jpg";
import techpal2 from "../../Assets/techPal2.jpg";
import cf_2 from "../../Assets/syep_2025.jpeg";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

const sliderImages = [
  { src: techpal1, alt: "Tech-Pal collaboration" },
  { src: syep2, alt: "Students building tech skills" },
  { src: techpal2, alt: "Tech-Pal program highlight" },
  { src: syepMain, alt: "Choicest Fruit internship moment" },
  { src: cf_1, alt: "SYEP internship showcase" },
  { src: cf_2, alt: "SYEP community learning" },
];

const pillars = [
  {
    title: "Peer Mentoring & Support",
    objective: "Create cross-continental youth connections for skill-sharing, cultural exchange, and leadership development.",
    bullets: [
      "Pair youth participants overseas with Choicest Fruit students for structured peer mentoring.",
      "Monthly virtual check-ins, collaborative mini-projects, and reflection activities.",
      "Showcase events where Tech-Pals present their projects to peers and partners.",
    ],
    outcomes: "Stronger global networks, confidence in communication, cultural exchange, and shared innovation experiences.",
  },
  {
    title: "Virtual Internships & Project Work",
    objective: "Provide international career exposure through remote, project-based internships.",
    bullets: [
      "Remote internships offered through Choicest Fruit and Austin-based partner companies.",
      "Co-develop projects in coding, electronics, UX/UI design, and ethical tech.",
      "Students gain mentorship from industry professionals while producing real-world deliverables.",
    ],
    outcomes: "Practical work experience, global career readiness, and expanded professional networks.",
  },
  {
    title: "Curriculum Exchange & Adaptation",
    objective: "Enrich educational programs by sharing best practices and adapting curricula to local contexts.",
    bullets: [
      "Joint curriculum review sessions to align with cultural, national, and industry needs.",
      "Blended learning pathways with co-taught lessons between organizations.",
      "Localized adaptations to ensure relevance and accessibility.",
    ],
    outcomes: "Enriched, context-relevant STEM education that reaches more students globally.",
  },
  {
    title: "Training & Capacity Building",
    objective: "Strengthen both student and instructor capabilities for long-term sustainability.",
    bullets: [
      "Develop shared masterclasses in software development, cybersecurity, entrepreneurship, and innovation leadership.",
      "Train Choicest Fruit Ambassadors in ethical tech development practices through partner expertise.",
      "Train instructors in Choicest Fruit’s Ethical Tech Development Framework and modules.",
    ],
    outcomes: "Empowered educators, scalable STEM programs, and sustainable global impact.",
  },
];

function TechPal() {
  const navigate = useNavigate();

  const handleApply = () => {
    navigate("/registration#techpal-section");
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2800,
    pauseOnHover: true,
    pauseOnFocus: true,
    adaptiveHeight: true,
  };

  const videoId = "5mPU-mIQgoQ";

  const { ref: topRef, hasEntered: topSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: overviewRef, hasEntered: overviewSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: pillarsRef, hasEntered: pillarsSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: videoRef, hasEntered: videoSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: testimonialsRef, hasEntered: testimonialsSeen } = useInViewOnce({ threshold: 0.18, once: true });

  return (
    <Container fluid className="about-section techpal-page">
      <Particle />

      <Container className="techpal-container">
        <header className="techpal-hero">
          <h1 className="techpal-hero-title">
            Tech<span className="purple">-</span>Pals
          </h1>
          <p className="techpal-hero-lead">
            A global initiative connecting young people across continents to learn, collaborate, and innovate through ethical
            technology.
          </p>
          <div className="techpal-hero-cta">
            <Button onClick={handleApply} className="custom-yellow-btn techpal-cta-btn">
              Apply Now
            </Button>
          </div>
        </header>

        <div ref={topRef} className={`techpal-reveal${topSeen ? " is-seen" : ""}`}>
          <Row className="techpal-top" style={{ justifyContent: "center" }}>
            <Col md={7} className="techpal-left">
              <div className="policy-card techpal-slider-card" aria-label="Tech-Pal highlights">
                <Slider {...settings}>
                  {sliderImages.map((image, index) => (
                    <div key={index} className="techpal-slide">
                      <img src={image.src} alt={image.alt} className="techpal-slide-img" />
                    </div>
                  ))}
                </Slider>
              </div>
            </Col>

            <Col md={5} className="techpal-right">
              <div className="policy-card techpal-summary-card">
                <h2 className="techpal-card-title">Program Snapshot</h2>
                <p className="about-text techpal-card-text">
                  Tech-Pals empowers youth to grow as ethical tech leaders through peer mentoring, virtual internships, curriculum
                  exchange, and capacity-building.
                </p>
                <ul className="techpal-bullets">
                  <li>Cross-continental collaboration</li>
                  <li>Project-based learning and mentorship</li>
                  <li>Leadership development through ethical tech</li>
                </ul>
                <Button onClick={handleApply} className="custom-yellow-btn techpal-cta-btn techpal-cta-btn-secondary">
                  Apply Here
                </Button>
              </div>

              <div className="policy-card techpal-image-card" aria-label="Tech-Pal portrait">
                <Tilt glareEnable={false}>
                  <img src={techpalPortrait} className="img-fluid techpal-image" alt="Tech-Pals at Choicest Fruit" />
                </Tilt>
              </div>
            </Col>
          </Row>
        </div>

        <section
          ref={overviewRef}
          className={`techpal-section techpal-reveal${overviewSeen ? " is-seen" : ""}`}
          aria-label="Tech-Pal overview"
        >
          <Row>
            <Col md={12}>
              <div className="policy-card techpal-copy-card">
                <h2 className="techpal-section-heading">Overview</h2>
                <p className="techpal-body">
                  The Tech-Pal Program is an international initiative of Choicest Fruit that connects young people across
                  continents to learn, collaborate, and innovate. Through peer mentoring, virtual internships, curriculum
                  exchange, and capacity building, Tech-Pals help youth grow as ethical leaders in technology while building
                  stronger, interconnected communities worldwide.
                </p>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={pillarsRef}
          className={`techpal-section techpal-reveal${pillarsSeen ? " is-seen" : ""}`}
          aria-label="Program pillars"
        >
          <h2 className="techpal-section-heading techpal-section-heading-center">Program Pillars</h2>
          <p className="techpal-lead">
            Each pillar is designed to build skills, expand networks, and create tangible outcomes for students and partners.
          </p>
          <div className="techpal-grid">
            {pillars.map((pillar) => (
              <div key={pillar.title} className="policy-card techpal-feature-card">
                <h3 className="techpal-feature-title">{pillar.title}</h3>
                <p className="techpal-feature-objective">
                  <strong className="purple">Objective:</strong> {pillar.objective}
                </p>
                <ul className="techpal-list">
                  {pillar.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="techpal-feature-outcomes">
                  <strong className="purple">Outcomes:</strong> {pillar.outcomes}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section
          ref={videoRef}
          className={`techpal-section techpal-reveal${videoSeen ? " is-seen" : ""}`}
          aria-label="Virtual internship program"
        >
          <h2 className="techpal-section-heading techpal-section-heading-center">Summer Internship Program</h2>
          <div className="policy-card techpal-video-card">
            <div className="techpal-video">
              <iframe
                title="Choicest Fruit program overview"
                src={`https://www.youtube.com/embed/${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section
          ref={testimonialsRef}
          className={`techpal-testimonials-section techpal-reveal${testimonialsSeen ? " is-seen" : ""}`}
          aria-label="Testimonials"
        >
          <h2 className="techpal-section-heading techpal-section-heading-center">Testimonials</h2>
          <div className="impact-testimonials">
            <Testimonials reviews={reviews} />
          </div>
        </section>
      </Container>
    </Container>
  );
}

export default TechPal;
